/**
 * src/constants/pageKeys.js
 *
 * LES CLES DE PAGE — source unique.
 *
 * Avant ce fichier, les cles vivaient a deux endroits sans lien : les 13 cles
 * projet dans pagePermissions.jsx, et les 8 cles `app.*` eparpillees dans les
 * attributs pageKey= de Routes.jsx. L'editeur de gabarits en avait besoin :
 * plutot qu'une troisieme copie, tout le monde lit desormais celle-ci.
 *
 * Trois consommateurs :
 *   Routes.jsx           — la garde RequirePage de chaque route
 *   pagePermissions.jsx  — l'ordre des pages projet, pour la redirection
 *   org-settings         — la liste a cocher de l'editeur
 *
 * Une cle ajoutee ici ne garde rien toute seule : il faut aussi poser le
 * pageKey sur la route. Mais elle apparaitra dans l'editeur, ce qui est
 * exactement ce qu'on veut pour une page a venir (voir `upcoming`).
 */

/** Constantes nommees — ce que Routes.jsx utilise, pour ne plus ecrire la chaine a la main. */
export const PAGE = {
  APP_PROJECT_LIST:  'app.project_list',
  APP_SCHEDULE:      'app.schedule',
  APP_DIRECTORY:     'app.directory',
  APP_INVOICE_INBOX: 'app.invoice_inbox',
  // La page n'existe pas encore : la cle est deja semee en base et apparait dans
  // l'editeur de gabarits. Ni route ni entree de menu tant qu'il n'y a rien a ouvrir.
  APP_TEAM_SCHEDULE: 'app.team_schedule',
  APP_PROJECT_VIEW:  'app.project_view',
  APP_BRAIN:         'app.brain',
  APP_ONX:           'app.onx',
  APP_MARGIN:        'app.margin',
  APP_USERS:         'app.users',
  APP_ORG_SETTINGS:  'app.org_settings',

  PROJECT_DIRECTORY:       'project.directory',
  PROJECT_DOCUMENTS:       'project.documents',
  PROJECT_SCHEDULE:        'project.schedule',
  PROJECT_DAILY_LOG:       'project.daily_log',
  PROJECT_RFI:             'project.rfi',
  PROJECT_BUDGET:          'project.budget',
  PROJECT_CONTRACTS:       'project.contracts',
  PROJECT_EXTRAS:          'project.extras',
  PROJECT_CHANGE_ORDERS:   'project.change_orders',
  PROJECT_EXPENSES:        'project.expenses',
  PROJECT_SUB_INVOICES:    'project.sub_invoices',
  PROJECT_SUB_INVOICE_APPROVE: 'project.sub_invoice_approve',
  PROJECT_CLIENT_INVOICES: 'project.client_invoices',
  PROJECT_QUITTANCES:      'project.quittances',
};

/**
 * Pages hors projet, dans l'ordre du menu principal puis des entrees d'en-tete.
 *
 * `labelKey` ne reutilise PAS sidebar.* : /profile, /users et /org-settings
 * n'ont aucune entree de menu, et deux cles projet tirent leur libelle
 * d'ailleurs. L'editeur a donc ses propres libelles, sous orgSettings.templates.
 *
 * `upcoming` marque une page dont la route n'existe pas encore : elle est
 * cochable des maintenant, elle ne garde rien tant qu'aucune route ne la porte.
 */
export const APP_PAGES = [
  { key: PAGE.APP_PROJECT_LIST,  labelKey: 'orgSettings.templates.pages.appProjectList' },
  { key: PAGE.APP_SCHEDULE,      labelKey: 'orgSettings.templates.pages.appSchedule' },
  { key: PAGE.APP_DIRECTORY,     labelKey: 'orgSettings.templates.pages.appDirectory' },
  { key: PAGE.APP_INVOICE_INBOX, labelKey: 'orgSettings.templates.pages.appInvoiceInbox' },
  { key: PAGE.APP_PROJECT_VIEW,  labelKey: 'orgSettings.templates.pages.appProjectView' },
  { key: PAGE.APP_BRAIN,         labelKey: 'orgSettings.templates.pages.appBrain' },
  { key: PAGE.APP_ONX,           labelKey: 'orgSettings.templates.pages.appOnx' },
  { key: PAGE.APP_MARGIN,        labelKey: 'orgSettings.templates.pages.appMargin', upcoming: true },
  { key: PAGE.APP_USERS,         labelKey: 'orgSettings.templates.pages.appUsers' },
  { key: PAGE.APP_ORG_SETTINGS,  labelKey: 'orgSettings.templates.pages.appOrgSettings' },
];

/**
 * Pages projet, dans l'ordre EXACT de la barre laterale. Cet ordre compte deux
 * fois : il dessine la liste a cocher, et il decide sur quelle page s'ouvre un
 * projet quand la premiere n'est pas accessible.
 *
 * `segment` est le morceau d'URL apres /project/:id/. `hidden` marque une page
 * atteinte depuis une autre, jamais depuis le menu.
 */
export const PROJECT_PAGES = [
  { key: PAGE.PROJECT_DIRECTORY,       segment: 'directory',       labelKey: 'orgSettings.templates.pages.projectDirectory' },
  { key: PAGE.PROJECT_DOCUMENTS,       segment: 'documents',       labelKey: 'orgSettings.templates.pages.projectDocuments' },
  { key: PAGE.PROJECT_SCHEDULE,        segment: 'schedule',        labelKey: 'orgSettings.templates.pages.projectSchedule' },
  { key: PAGE.PROJECT_DAILY_LOG,       segment: 'daily-log',       labelKey: 'orgSettings.templates.pages.projectDailyLog' },
  { key: PAGE.PROJECT_RFI,             segment: 'rfi',             labelKey: 'orgSettings.templates.pages.projectRfi' },
  { key: PAGE.PROJECT_BUDGET,          segment: 'budget',          labelKey: 'orgSettings.templates.pages.projectBudget' },
  { key: PAGE.PROJECT_CONTRACTS,       segment: 'contracts',       labelKey: 'orgSettings.templates.pages.projectContracts' },
  { key: PAGE.PROJECT_EXTRAS,          segment: 'extras',          labelKey: 'orgSettings.templates.pages.projectExtras' },
  { key: PAGE.PROJECT_CHANGE_ORDERS,   segment: 'change-orders',   labelKey: 'orgSettings.templates.pages.projectChangeOrders' },
  { key: PAGE.PROJECT_EXPENSES,        segment: 'expenses',        labelKey: 'orgSettings.templates.pages.projectExpenses' },
  { key: PAGE.PROJECT_SUB_INVOICES,    segment: 'sub-invoices',    labelKey: 'orgSettings.templates.pages.projectSubInvoices' },
  { key: PAGE.PROJECT_SUB_INVOICE_APPROVE, segment: null,          labelKey: 'orgSettings.templates.pages.projectSubInvoiceApprove', hidden: true },
  { key: PAGE.PROJECT_CLIENT_INVOICES, segment: 'client-invoices', labelKey: 'orgSettings.templates.pages.projectClientInvoices' },
  { key: PAGE.PROJECT_QUITTANCES,      segment: 'quittances',      labelKey: 'orgSettings.templates.pages.projectQuittances' },
];

/** Les deux groupes, pour l'editeur. */
export const PAGE_GROUPS = [
  { id: 'app',     titleKey: 'orgSettings.templates.groupApp',     pages: APP_PAGES },
  { id: 'project', titleKey: 'orgSettings.templates.groupProject', pages: PROJECT_PAGES },
];

export const ALL_PAGES = [...APP_PAGES, ...PROJECT_PAGES];

/** Les seules pages projet qui peuvent servir de porte d'entree a un projet. */
export const PROJECT_PAGE_ORDER = PROJECT_PAGES?.filter((p) => p?.segment);

export default PAGE;
